import { Injectable, signal } from '@angular/core';

const STORAGE_KEY = 'hb.sidebar.collapsed';

/**
 * Whether the desktop sidebar is collapsed to an icon rail.
 *
 * It lives in a service rather than the sidebar because three places have to agree on it: the
 * sidebar's own width, the fixed header's left edge, and the main content's left margin. If any of
 * them read a different value the layout tears — content sliding under the rail, or a gap beside it.
 *
 * Desktop only. Below lg the sidebar is a drawer that slides over the content, so there is nothing
 * to collapse and the stored value is simply ignored.
 */
@Injectable({ providedIn: 'root' })
export class SidebarStateService {
    private readonly collapsedSignal = signal(this.read());
    readonly collapsed = this.collapsedSignal.asReadonly();

    toggle(): void {
        this.collapsedSignal.update((v) => !v);
        this.write(this.collapsedSignal());
    }

    /** Reading a preference must never be what stops the app rendering, so storage failures
     *  (private mode, blocked cookies, SSR) fall back to the expanded default. */
    private read(): boolean {
        try {
            return localStorage.getItem(STORAGE_KEY) === '1';
        } catch {
            return false;
        }
    }

    private write(collapsed: boolean): void {
        try {
            localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
        } catch {
            /* Preference is lost for next visit; the current session still works. */
        }
    }
}
